import dayjs from 'dayjs';

import { currentYear } from './years';

/**
 * What a Health Care Card's expiry date means for the discount.
 *
 * The rule used to be that the card had to last beyond 30 November, and a parent whose card
 * expired before then could not go forward at all. That is stricter than the school needs: a
 * card that is valid today is valid today, and most parents whose card is about to lapse will
 * have a new one before the fees fall due. Being stopped at that point leaves them with no way
 * through the form and a phone call to make.
 *
 * So an expired card is refused — it proves nothing — and a card expiring before the deadline is
 * allowed through with a warning saying what will happen.
 *
 * The date is also checked again whenever it is read back. A parent who chose a valid date in
 * September and returns in December has a date that was fine when they picked it and is not any
 * more, and nothing would otherwise notice.
 */

export const HCC_STATUS = {
    NONE: 'none',
    EXPIRED: 'expired',
    EXPIRING: 'expiring',
    VALID: 'valid',
};

/** The date the school stops applying the discount automatically. */
export const discountDeadline = (year = currentYear()) => dayjs(`${year}-11-30`).endOf('day');

/**
 * The earliest date that can be chosen: tomorrow.
 *
 * Neither a past date nor today's is accepted — a card expiring today has nothing left to run,
 * and the school is asking for one that is still valid going forward.
 */
export const earliestExpiry = (today = dayjs()) => dayjs(today).add(1, 'day').startOf('day');

/**
 * Where a card's expiry stands.
 *
 * `today` is a parameter so this can be tested, and so a date picked months ago is judged
 * against the day it is being read rather than the day it was chosen.
 */
export const hccExpiryState = (expiry, today = dayjs()) => {
    if (!expiry) {
        return { status: HCC_STATUS.NONE, daysLeft: null };
    }

    const date = dayjs(expiry);
    if (!date.isValid()) {
        return { status: HCC_STATUS.NONE, daysLeft: null };
    }

    const startOfToday = dayjs(today).startOf('day');
    const endOfExpiry = date.endOf('day');
    const daysLeft = endOfExpiry.diff(startOfToday, 'day');

    // Today is not accepted either: a card expiring today has nothing left to run.
    if (!date.startOf('day').isAfter(startOfToday)) {
        return { status: HCC_STATUS.EXPIRED, daysLeft };
    }

    if (endOfExpiry.isBefore(discountDeadline(dayjs(today).year()))) {
        return { status: HCC_STATUS.EXPIRING, daysLeft };
    }

    return { status: HCC_STATUS.VALID, daysLeft };
};

export const isHccExpired = (expiry, today = dayjs()) =>
    hccExpiryState(expiry, today).status === HCC_STATUS.EXPIRED;

/** The addresses a parent is asked to contact, by campus. */
export const arOfficerEmail = (college) => ({
    Kewdale: 'ark@aic.wa.edu.au',
    Thornlie: 'art@aic.wa.edu.au',
    Dianella: 'ard@aic.wa.edu.au',
    Adelaide: 'ara@aic.wa.edu.au',
    Forrestdale: 'arf@aic.wa.edu.au',
    'Henley Brook': 'arh@aic.wa.edu.au',
}[college] || '');

/**
 * What to tell the parent. Null when there is nothing worth saying.
 *
 * The expired message asks for the current card rather than explaining the rule: the parent's
 * next action is to find the right date, not to understand the policy.
 */
export const hccExpiryMessage = (expiry, { college = '', today = dayjs() } = {}) => {
    const { status } = hccExpiryState(expiry, today);
    const officer = arOfficerEmail(college);

    if (status === HCC_STATUS.EXPIRED) {
        return {
            severity: 'error',
            text: 'The expiry date must be after today. Please enter the date shown on your '
                + 'current Health Care Card, and upload a picture of it.',
        };
    }

    if (status === HCC_STATUS.EXPIRING) {
        // What actually happens, rather than what the old rule implied: they go through now, and
        // the card is checked when the year is billed. Saying "the discount will be applied"
        // would promise something this screen cannot decide.
        return {
            severity: 'warning',
            text: `This Health Care Card expires before 30 November ${dayjs(today).year()}. You can `
                + 'continue with your re-enrolment now. The card is checked again when the school '
                + 'year is billed, so please send your renewed card to the AR officer'
                + `${officer ? ` at ${officer}` : ''} when you have it — without a valid card at that `
                + 'point, the discount cannot be applied to the yearly fees.',
        };
    }

    return null;
};
